import dayjs from 'dayjs'
import { InsightsCapabilities } from '@pushly/insights-sdk/lib/insights-capabilities'
import { DashboardDomain, GoodDirection } from '../../types'
import {
    DailyMeasure,
    DailyRow,
    dailyLevelChanges,
    dailyLevels,
    dailyRates,
    dailySeries,
    levelOn,
    levelPairOn,
    sumMeasure,
    zeroWhereNoDayHasAnythingToDivideBy,
} from '../../math/series'
import { levelTrend, rateTrend, trend } from '../../math/statistics'
import { DateWindow, baselineWindowFor, comparisonWindowFor, lengthInDays, shiftIsoDate } from '../../math/windows'
import {
    MINUS,
    NO_VALUE,
    formatCount,
    formatCountInFull,
    formatMoney,
    formatMoneyInFull,
    formatRate,
    currencyListFor,
    formatShortDateRange,
} from '../../math/format'
import { isHeadlineTileWithheld } from '../../data/withheld-measures'
import { DashboardTemplate, HeadlineTileSpec, TEMPLATE_SLOTS } from '../../template'
import { assertUnreachable } from '../../assert-unreachable'
import { ENGAGEMENT_ATTRIBUTION_NOTE, SENDS_ATTRIBUTION_NOTE, withAttributionNote } from '../../attribution-notes'
import type { SubscriberHistoryAvailability } from '../../data/use-org-dashboard-series'

export type HeadlineTile = {
    key: string
    label: string
    formattedValue: string
    /** The figure written out in full, set only when `formattedValue` is abbreviated to K or M so there is something to reveal. */
    fullValue?: string
    /** A fraction for `count` tiles (0.142 is +14.2%) and percentage points for `rate` tiles; null when there is nothing to compare with. */
    delta: { value: number | null; kind: 'count' | 'rate'; steady: boolean }
    /** The change in the tile's own units with the comparison window in words, or why there is none. */
    absoluteDelta: string
    /** The delta's comparison window in words, for assistive text. */
    comparisonLabel: string
    /** The last 14 days ending on the range's last day. A `null` day had no data. */
    sparkline: Array<number | null>
    supporting: string
    definition: string
    /** Undefined when the registry names no direction for the measure; such a tile is not coloured. */
    goodDirection: GoodDirection | undefined
    withheld?: { reason: string; detail: string }
}

export type HeadlineRegistry = {
    notifications?: InsightsCapabilities
    prompts?: InsightsCapabilities
    goodDirectionFor: (measure: string) => GoodDirection | undefined
}

const SPARKLINE_DAYS = 14
const NO_COMPARISON_PERIOD_YET = 'No comparison period yet'
const MIXED_CURRENCIES = 'Mixed currencies'
const CURRENCY_NOT_SET = 'Currency not set'
const NO_SUBSCRIBER_HISTORY = 'No subscriber history'
const SUBSCRIBER_HISTORY_UNAVAILABLE = 'Subscriber history unavailable'

/**
 * Subscriber totals and revenue per send have no measure of their own in the registry. A total moves with
 * subscriptions and revenue per send with revenue, so each takes the direction of the measure it follows.
 */
const GOOD_DIRECTION_MEASURE_OVERRIDES: Record<string, string> = {
    total_subscribers: 'subscriptions',
    revenue_per_send: 'purchase_amount',
}

const signed = (amount: number, format: (magnitude: number) => string): string =>
    amount > 0 ? `+${format(amount)}` : amount < 0 ? `${MINUS}${format(-amount)}` : format(0)

const domainsIn = (domains: DashboardDomain[], selectedDomainIds: number[]): DashboardDomain[] =>
    selectedDomainIds.length ? domains.filter((domain) => selectedDomainIds.includes(domain.id)) : domains

/**
 * The distinct currencies of the selected domains (every domain when none is selected), sorted, with an unset
 * currency (undefined) last. An unset currency is a currency of its own: it is not assumed to be any other.
 */
export const currenciesIn = (domains: DashboardDomain[], selectedDomainIds: number[]): Array<string | undefined> =>
    [...new Set(domainsIn(domains, selectedDomainIds).map((domain) => domain.currencyCode))].sort()

const lastDays = <Value>(values: Value[]): Value[] => values.slice(-SPARKLINE_DAYS)

const sparklineWindow = (range: DateWindow): DateWindow => ({
    since: shiftIsoDate(range.through, -(SPARKLINE_DAYS - 1)),
    through: range.through,
})

type TileContext = {
    spec: HeadlineTileSpec
    rows: DailyRow[]
    domains: DashboardDomain[]
    scope: number[]
    selectedDomainIds: number[]
    range: DateWindow
    comparisonWindow: DateWindow
    baselineWindow: DateWindow
    hasComparisonRows: boolean
    registry: HeadlineRegistry
    currencyCode: string | undefined
    subscriberHistory: SubscriberHistoryAvailability
}

const goodDirectionOf = ({ spec, registry }: TileContext): GoodDirection | undefined =>
    registry.goodDirectionFor(GOOD_DIRECTION_MEASURE_OVERRIDES[spec.key] ?? spec.measure)

const registryLabelFor = ({ spec, registry }: TileContext): string => {
    const capabilities = spec.entity === 'prompts' ? registry.prompts : registry.notifications
    return capabilities?.getField(spec.measure)?.label ?? spec.label
}

const withheldTile = (context: TileContext, reason: string, detail: string): HeadlineTile => ({
    key: context.spec.key,
    label: context.spec.label,
    formattedValue: NO_VALUE,
    delta: { value: null, kind: 'count', steady: false },
    absoluteDelta: '',
    comparisonLabel: '',
    sparkline: [],
    supporting: '',
    definition: definitionFor(context),
    goodDirection: goodDirectionOf(context),
    withheld: { reason, detail },
})

const RATE_DEFINITIONS: Record<string, string> = {
    ctr: 'CTR = clicks ÷ impressions',
    subscription_rate: 'subscriptions ÷ permissions shown',
    purchase_rate: 'purchases ÷ clicks',
    revenue_per_send: 'revenue ÷ notifications sent',
}

/** The note on what day a tile's figure is counted on; subscriber figures are counted on the day they happened, which needs no note. */
const ATTRIBUTION_NOTE_BY_TILE: Partial<Record<HeadlineTileSpec['key'], string>> = {
    clicks: ENGAGEMENT_ATTRIBUTION_NOTE,
    ctr: ENGAGEMENT_ATTRIBUTION_NOTE,
    purchases: ENGAGEMENT_ATTRIBUTION_NOTE,
    revenue: ENGAGEMENT_ATTRIBUTION_NOTE,
    revenue_per_send: ENGAGEMENT_ATTRIBUTION_NOTE,
    sends: SENDS_ATTRIBUTION_NOTE,
}

const definitionFor = (context: TileContext): string => {
    if (context.spec.key === 'total_subscribers') return 'Subscribers active on the last day of the range'
    const definition = RATE_DEFINITIONS[context.spec.key] ?? registryLabelFor(context)
    const attributionNote = ATTRIBUTION_NOTE_BY_TILE[context.spec.key]
    return attributionNote ? withAttributionNote(definition, attributionNote) : definition
}

const unavailableComparison = (kind: 'count' | 'rate') => ({
    delta: { value: null, kind, steady: false },
    absoluteDelta: NO_COMPARISON_PERIOD_YET,
})

const formatterFor = (spec: HeadlineTileSpec, currencyCode: string | undefined) => (amount: number) =>
    spec.unit === 'money' ? formatMoney(amount, currencyCode) : formatCount(amount)

const ABBREVIATED_FIGURE = /[KM]$/

/** The full figure for a tile whose compact figure hides digits, and nothing for one that already shows them all. */
const fullValueWhenAbbreviated = (formattedValue: string, fullValue: string): { fullValue?: string } =>
    ABBREVIATED_FIGURE.test(formattedValue) ? { fullValue } : {}

type FlowTileSpec = Extract<HeadlineTileSpec, { kind: 'flow' }>
type RatioTileSpec = Extract<HeadlineTileSpec, { kind: 'ratio' }>

/** The line under a flow tile's figure. Only the tile being built reads the totals it needs. */
const flowSupporting = (context: TileContext, spec: FlowTileSpec, flowTotal: number): string => {
    const rangeTotal = (measure: DailyMeasure) => sumMeasure(context.rows, measure, context.range, context.scope)
    switch (spec.key) {
        case 'clicks':
            return `on ${formatCount(rangeTotal('impressions'))} impressions`
        case 'new_subscribers': {
            const permissionsShown = rangeTotal('permissionsShown')
            return `from ${formatCount(permissionsShown)} permissions shown (${formatRate(
                permissionsShown ? flowTotal / permissionsShown : null,
            )})`
        }
        case 'sends':
            return sendingDomainsSummary(context)
        case 'purchases': {
            const clicks = rangeTotal('clicks')
            return `${formatRate(clicks ? flowTotal / clicks : null, { purchaseRate: true })} of clicks`
        }
        case 'revenue':
            return `${formatCount(rangeTotal('purchases'))} purchases`
        default:
            return assertUnreachable(spec.key)
    }
}

const flowTile = (context: TileContext, spec: FlowTileSpec): HeadlineTile => {
    const { rows, scope, range, comparisonWindow, baselineWindow, currencyCode } = context
    const flowMeasure = spec.dailyMeasure
    const format = formatterFor(spec, currencyCode)
    const current = dailySeries(rows, flowMeasure, range, scope)
    const flowTrend = trend(
        current,
        dailySeries(rows, flowMeasure, comparisonWindow, scope),
        dailySeries(rows, flowMeasure, baselineWindow, scope),
    )
    const comparison = context.hasComparisonRows
        ? {
              delta: { value: flowTrend.deltaPct, kind: 'count' as const, steady: flowTrend.steady },
              absoluteDelta: `${signed(flowTrend.deltaAbs, format)} vs ${formatShortDateRange(comparisonWindow)}`,
          }
        : unavailableComparison('count')
    return {
        key: spec.key,
        label: spec.label,
        formattedValue: format(flowTrend.current),
        ...fullValueWhenAbbreviated(
            format(flowTrend.current),
            spec.unit === 'money'
                ? formatMoneyInFull(flowTrend.current, currencyCode)
                : formatCountInFull(flowTrend.current),
        ),
        ...comparison,
        comparisonLabel: `vs ${formatShortDateRange(comparisonWindow)}`,
        sparkline: lastDays(dailySeries(rows, flowMeasure, sparklineWindow(range), scope)),
        supporting: flowSupporting(context, spec, flowTrend.current),
        definition: definitionFor(context),
        goodDirection: goodDirectionOf(context),
    }
}

const sendingDomainsSummary = ({ rows, domains, selectedDomainIds, range }: TileContext): string => {
    const selectedDomains = domainsIn(domains, selectedDomainIds)
    const sendingDomainIds = new Set(
        rows
            .filter((row) => row.date >= range.since && row.date <= range.through && row.sends > 0)
            .map((row) => row.domainId),
    )
    const sendingCount = selectedDomains.filter((domain) => sendingDomainIds.has(domain.id)).length
    return `notifications sent by ${sendingCount} of ${selectedDomains.length} domains`
}

const ratioTile = (context: TileContext, spec: RatioTileSpec): HeadlineTile => {
    const { numerator, denominator, denominatorNoun } = spec
    const { rows, scope, range, comparisonWindow, baselineWindow, currencyCode } = context
    const totals = (window: DateWindow) => ({
        numerator: sumMeasure(rows, numerator, window, scope),
        denominator: sumMeasure(rows, denominator, window, scope),
    })
    const currentTotals = totals(range)
    const priorTotals = totals(comparisonWindow)
    const ratioTrend = rateTrend(
        currentTotals.numerator,
        currentTotals.denominator,
        priorTotals.numerator,
        priorTotals.denominator,
        dailyRates(rows, numerator, denominator, baselineWindow, scope),
    )
    const isMoney = spec.unit === 'money'
    const formatRatio = (ratio: number | null) =>
        ratio === null ? NO_VALUE : isMoney ? formatMoney(ratio, currencyCode) : formatRate(ratio)
    const { current: currentRatio, prior: priorRatio } = ratioTrend
    const canCompare = context.hasComparisonRows && priorRatio !== null && currentRatio !== null
    const comparison = !canCompare
        ? {
              ...unavailableComparison(isMoney ? 'count' : 'rate'),
              // A comparison period exists but a ratio needs something to divide by.
              ...(context.hasComparisonRows ? { absoluteDelta: `No ${denominatorNoun} in this period` } : {}),
          }
        : isMoney
          ? {
                // A money ratio is compared as a relative change; percentage points only make sense for a share.
                delta: {
                    value: priorRatio === 0 ? null : (currentRatio - priorRatio) / priorRatio,
                    kind: 'count' as const,
                    steady: ratioTrend.steady,
                },
                absoluteDelta: `was ${formatRatio(priorRatio)} in ${formatShortDateRange(comparisonWindow)}`,
            }
          : {
                delta: { value: ratioTrend.deltaPts, kind: 'rate' as const, steady: ratioTrend.steady },
                absoluteDelta: `was ${formatRatio(priorRatio)} in ${formatShortDateRange(comparisonWindow)}`,
            }
    const supporting = isMoney
        ? `${formatMoney(currentTotals.numerator, currencyCode)} ÷ ${formatCount(currentTotals.denominator)} sends`
        : `${formatCount(currentTotals.numerator)} clicks ÷ ${formatCount(currentTotals.denominator)} impressions`
    return {
        key: spec.key,
        label: spec.label,
        formattedValue: formatRatio(ratioTrend.current),
        ...(isMoney && ratioTrend.current !== null
            ? fullValueWhenAbbreviated(
                  formatRatio(ratioTrend.current),
                  formatMoneyInFull(ratioTrend.current, currencyCode),
              )
            : {}),
        ...comparison,
        comparisonLabel: `vs ${formatShortDateRange(comparisonWindow)}`,
        sparkline: zeroWhereNoDayHasAnythingToDivideBy(
            lastDays(dailyRates(rows, numerator, denominator, sparklineWindow(range), scope)),
        ),
        supporting,
        definition: definitionFor(context),
        goodDirection: goodDirectionOf(context),
    }
}

const totalSubscribersTile = (context: TileContext): HeadlineTile => {
    const { spec, rows, domains, scope, selectedDomainIds, range, comparisonWindow, baselineWindow } = context
    // Without the levels a failed read and an organization with no history look the same in the rows, so the read's
    // outcome decides which reason the reader is given.
    if (context.subscriberHistory === 'unavailable') {
        return withheldTile(
            context,
            SUBSCRIBER_HISTORY_UNAVAILABLE,
            'Subscriber history could not be loaded. Refresh to try again.',
        )
    }
    const currentLevel = levelOn(rows, range.through, scope)
    if (currentLevel === null) {
        return withheldTile(
            context,
            NO_SUBSCRIBER_HISTORY,
            'None of the selected domains has a subscriber count for the last day of this range.',
        )
    }
    // The shown total is every domain sampled on the last day; its change is taken over the domains sampled on both
    // dates, so a domain whose snapshot is late is not read as a loss.
    const levelPair = levelPairOn(rows, comparisonWindow.through, range.through, scope)
    const levelChange = levelTrend(
        levelPair?.current ?? null,
        levelPair?.prior ?? null,
        dailyLevelChanges(rows, baselineWindow, scope),
        lengthInDays(range),
    )
    const comparison =
        levelPair === null
            ? unavailableComparison('count')
            : {
                  delta: { value: levelChange.deltaPct, kind: 'count' as const, steady: levelChange.steady },
                  absoluteDelta: `${signed(levelChange.deltaAbs, formatCount)} vs ${formatShortDateRange(comparisonWindow)}`,
              }
    const selectedDomains = domainsIn(domains, selectedDomainIds)
    const domainsWithLevel = new Set(
        rows.filter((row) => row.date === range.through && row.subscribers !== null).map((row) => row.domainId),
    )
    const sampledCount = selectedDomains.filter((domain) => domainsWithLevel.has(domain.id)).length
    return {
        key: spec.key,
        label: spec.label,
        formattedValue: formatCount(currentLevel),
        ...fullValueWhenAbbreviated(formatCount(currentLevel), formatCountInFull(currentLevel)),
        ...comparison,
        comparisonLabel: `vs ${formatShortDateRange(comparisonWindow)}`,
        sparkline: dailyLevels(rows, sparklineWindow(range), scope),
        // The coverage is only worth saying when some selected domain has no count on the last day and so is missing from the total.
        supporting:
            `active on ${dayjs(range.through).format('MMM D')}` +
            (sampledCount < selectedDomains.length
                ? `, counting ${sampledCount} of ${selectedDomains.length} domains`
                : ''),
        definition: definitionFor(context),
        goodDirection: goodDirectionOf(context),
    }
}

/**
 * The headline tiles for the template, computed from the daily rows. Flow measures are summed over the range and
 * compared with the same number of days before it; ratios are recomputed from summed parts; the subscriber total
 * is the level on the range's last day compared with the level a comparison window earlier.
 */
export const buildHeadlineTiles = (args: {
    template: DashboardTemplate
    rows: DailyRow[]
    domains: DashboardDomain[]
    /** Empty means every domain. */
    selectedDomainIds: number[]
    range: DateWindow
    /** Last Month is compared with the calendar month before it; see `comparisonWindowFor`. */
    rangeKey?: string
    registry: HeadlineRegistry
    subscriberHistory: SubscriberHistoryAvailability
}): HeadlineTile[] => {
    const { template, rows, domains, selectedDomainIds, range, rangeKey, registry, subscriberHistory } = args
    // Series rows are only counted for domains the account service listed, so the tiles agree with the tables below them.
    const scope = selectedDomainIds.length ? selectedDomainIds : domains.map((domain) => domain.id)
    const comparisonWindow = comparisonWindowFor(range, rangeKey)
    const currencies = currenciesIn(domains, selectedDomainIds)
    const hasComparisonRows = rows.some(
        (row) =>
            row.date >= comparisonWindow.since && row.date <= comparisonWindow.through && scope.includes(row.domainId),
    )

    return TEMPLATE_SLOTS[template].headlineTiles
        .filter((spec) => !isHeadlineTileWithheld(spec, registry))
        .map((spec) => {
            const context: TileContext = {
                spec,
                rows,
                domains,
                scope,
                selectedDomainIds,
                range,
                comparisonWindow,
                baselineWindow: baselineWindowFor(range),
                hasComparisonRows,
                registry,
                currencyCode: currencies[0],
                subscriberHistory,
            }
            if (spec.unit === 'money' && currencies.length > 1) {
                return withheldTile(
                    context,
                    MIXED_CURRENCIES,
                    `Selected domains report in ${currencyListFor(currencies)}. Filter to one currency to see a total.`,
                )
            }
            if (spec.unit === 'money' && context.currencyCode === undefined) {
                return withheldTile(
                    context,
                    CURRENCY_NOT_SET,
                    'The selected domains have no currency set, so revenue cannot be shown.',
                )
            }
            switch (spec.kind) {
                case 'level':
                    return totalSubscribersTile(context)
                case 'ratio':
                    return ratioTile(context, spec)
                case 'flow':
                    return flowTile(context, spec)
                default:
                    return assertUnreachable(spec)
            }
        })
}
